import {Component, OnInit} from '@angular/core';
import {SectionBase} from '../../../components/section/section-base';
import {AgreementsSectionInterface} from '../../../../core/services/api/sections-api/agreements-section.interface';
import {AgreementsStateService} from '../../../../core/services/impl/section-services/agreements/agreements-state.service';
import {AgreementModel} from '../../../../core/models/agreement.model';
import {Offer} from '../../../../core/models/offer/offer.model';
import {AgreementsSectionConfig} from './agreements-section.config';
import {TranslateService} from '@ngx-translate/core';
import {ControlChange} from '../../../../core/models/control-change.model';
import {MetricsService} from '../../../services/metrics.service';
import {CustomMetricsName} from '../../../components/models/metrics/custom-metrics-name.enum';
import {MetricsType} from '../../../components/models/metrics/metrics-type.enum';
import {RegistrationService} from '../../../../core/services/impl/registration.service';
import {RegistrationStorageService} from '../../../services/registration-storage.service';
import {AgreementsService} from '../../../services/agreements.service';
import {isNext, isMPay, isSkycash} from '../../../../core/utils/utils';
import {UrlParamsService} from '../../../../core/services/impl/url-params.service';
import {ApplicationStateService} from '../../../services/application-state.service';
import {InsuranceCompanyCode} from '../../../models/insurance-company-code.enum';
import {LanguageService} from '../../../services/language.service';
import {GoogleAnalyticsEventsService} from '../../../services/google-analytics-events.service';
import {GA4EventKey} from '../../../../core/consts/ga4-event-key.enum';
import {CheckboxesGroupConfig} from '../../../components/checkboxes-group/checkboxes-group.component';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';

@Component({
  selector: 'app-agreements-section',
  templateUrl: './agreements-section.component.html',
  styleUrls: ['./agreements-section.component.scss']
})
export class AgreementsSectionComponent extends SectionBase implements OnInit, AgreementsSectionInterface {

  editableAgreements: AgreementModel[];
  readonlyAgreements: AgreementModel[];
  checkboxesGroupConfig: CheckboxesGroupConfig;
  checkboxesGroupFieldKeys: string[];
  isNext = isNext();
  sectionVisible: boolean;

  private offer: Offer;
  private config: AgreementsSectionConfig = new AgreementsSectionConfig();

  get language(): string {
    return this.languageService.getDefaultLanguage().toUpperCase();
  }

  get shouldSkipPowerOfAttorneyAgreement(): boolean {
    return (isMPay() || isSkycash()) && this.urlParamsService.isMobileOrigin;
  }

  get agreementsRequiredSymbol(): string {
    return this.agreementsService.REQUIRED_SYMBOL;
  }

  get allAgreements(): AgreementModel[] {
    return [
        ...this.editableAgreements,
        ...this.readonlyAgreements
    ]
        .filter(agreement => isNotNullOrUndefined(agreement));
  }

  constructor(private translateService: TranslateService,
              private stateService: AgreementsStateService,
              private appStateService: ApplicationStateService,
              public agreementsService: AgreementsService,
              private googleAnalyticsService: GoogleAnalyticsEventsService,
              private languageService: LanguageService,
              private urlParamsService: UrlParamsService,
              private registrationStorage: RegistrationStorageService,
              private registrationService: RegistrationService,
              private metricsService: MetricsService) {
    super();
    this.stateService.agreementsSection = this;
  }

  ngOnInit() {
    this.stateService.init();
    this.stateService.$initCompleted.subscribe(() => {
      this.initAgreements();
      this.initSectionVisible();
      this.initCheckboxesGroup();
      this.initBean();
      this.createConfig();
      this.createOfferAgreementsConfigs();
      this.initOffer();
      this.buildForm();
      this.initControlsChanges();
      this.registrationStorage.updateRegistration();
    })
  }

  initControlsChanges() {
    let controlsChanges: ControlChange[] = [];
    this.stateService.offerAgreements.forEach(item => {
      if (item.details.hasOwnProperty('readonly') && !item.details.readonly) {
        controlsChanges.push({
          controlName: item.code,
          behaviour: value => {
            if (this.getControl(item.code).touched) {
              this.formFactoryService.validateField(item.code);
            }
            this.stateService.changeAgreement(item.code, value);
          }
        });
      }
    });
    this.handleControlsState(...controlsChanges);
  }

  createConfig() {
    this.formConfig = this.config.createSectionConfig(this.languageService.getDefaultLanguage(), this.agreementsService);
  }

  initBean() {
    if (this.editableAgreements) {
      this.bean = this.editableAgreements;
    }
  }

  changeAgreement(code: string, selected: boolean): void {
  }

  onCheckAllAgreementsChange(checked: boolean): void {
    this.metricsService.sendSimpleMetric(CustomMetricsName.ALL_OFFER_AGREEMENTS, MetricsType.CHECKBOX, JSON.stringify(this.getMetricValue(checked)));
    this.onFieldChange(null);
  }

  private initSectionVisible(): void {
    this.sectionVisible = this.allAgreements.length > 0;
  }

  private initAgreements(): void {
    this.readonlyAgreements = this.stateService.offerAgreements.filter(agreement => agreement.details.hasOwnProperty('readonly') && agreement.details.readonly);
    this.editableAgreements = this.stateService.offerAgreements.filter(agreement => agreement.details.hasOwnProperty('readonly') && !agreement.details.readonly);

    if (this.shouldSkipPowerOfAttorneyAgreement) {
      this.readonlyAgreements = this.readonlyAgreements.filter(agreement => !agreement.code.includes('POWER_OF_ATTORNEY'));
    }
  }

  private initOffer() {
    if (this.stateService.offer) {
      this.offer = this.stateService.offer;
    }
  }

  private createOfferAgreementsConfigs() {
    if (this.editableAgreements) {
      this.editableAgreements.forEach(agreement => {
          this.formConfig.fieldsConfig.push(this.config.createOfferAgreementConfig(agreement));
        }
      );
    }
  }

  private getMetricValue(allAgreementsValue: boolean): any {
    const codes = this.editableAgreements.map(agreement => agreement.code);
    return {
      codes: codes,
      value: allAgreementsValue
    };
  }

  private getAgreementValue(agreement: AgreementModel): string {
    const agreementValues = agreement.details.values.filter(item => item.languageCode.toUpperCase() === this.language);
    if (agreementValues.length > 0) {
      return agreementValues[0].value;
    } else {
      throw new Error(`Agreement does not contain value for given language code: ${this.language}.`);
    }
  }

  private initCheckboxesGroup(): void {
    this.checkboxesGroupConfig = {
      checkAllLabel: this.appStateService.insuranceCompanyCode === InsuranceCompanyCode.LINK4 ? 'SHARED.AGREEMENTS.checkAllStatements' : 'SHARED.AGREEMENTS.checkAllAgreements'
    };
    this.checkboxesGroupFieldKeys = this.editableAgreements.map(agreement => agreement.code);
  }
}
